import { useMemo, useState } from "react";
import { Select } from "antd";
import { GlobalOutlined } from "@ant-design/icons";
import { Button, ButtonColorType } from "@/ui-components/commonComponents/Button/Button";
import { BasicModal } from "@/ui-components/commonComponents/Modal/BasicModal";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { localeLabel } from "@/shared/utils/locale.utils";
import styles from "./styles.module.css";

interface DevelopLanguageControlProps {
  availableLocales: LanguagesIsoEnum[];
  locale: LanguagesIsoEnum;
  onChange: (locale: LanguagesIsoEnum) => void;
}

// Header control (sits next to PRINT): shows the current display language plus a "Change" link that
// opens a modal to pick another. The choice only applies on confirm — and, per the copy, also sets
// the language the report prints in. Screen-only; the parent omits it on the print path.
//
// This control is admin-only navigation chrome (never printed/shared), so its copy and language names
// stay in English regardless of the report's selected language — hardcoded, not routed through the
// i18n catalog.
export default function DevelopLanguageControl({ availableLocales, locale, onChange }: DevelopLanguageControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingLocale, setPendingLocale] = useState<LanguagesIsoEnum>(locale);

  const languageOptions = useMemo(
    () =>
      availableLocales.map((available) => ({
        value: available,
        label: localeLabel(available),
      })),
    [availableLocales],
  );

  const openModal = () => {
    setPendingLocale(locale);
    setIsOpen(true);
  };

  const confirm = () => {
    onChange(pendingLocale);
    setIsOpen(false);
  };

  return (
    <span className={styles.languageControl}>
      <GlobalOutlined aria-hidden="true" />
      <span className={styles.languageControlCurrent}>{localeLabel(locale).toUpperCase()}</span>
      <button type="button" className={styles.languageControlChange} onClick={openModal}>
        Change
      </button>
      <BasicModal
        isModalOpen={isOpen}
        handleCancel={() => setIsOpen(false)}
        footer={
          <Button colorType={ButtonColorType.dark} onClick={confirm}>
            Confirm
          </Button>
        }
      >
        <div>
          <h2 className={styles.modalTitle}>Change Language</h2>
          <hr className={styles.modalDivider} />
          <p className={styles.modalBodyText}>
            This information is available in the languages below. Please note that changing the language here will also
            set the language that the report prints in.
          </p>
          <Select<LanguagesIsoEnum>
            className={styles.modalSelect}
            value={pendingLocale}
            onChange={(value) => setPendingLocale(value)}
            aria-label="Change Language"
            options={languageOptions}
          />
        </div>
      </BasicModal>
    </span>
  );
}
